import type { ComponentType } from "react";
import React, { useEffect, useRef } from "react";
import { useRootDispatch, useRootSelector } from "~redux/hooks";
import {
  colorNameMap,
  eramFontDimensionMap,
  eramFontNameMap,
  useFocused,
} from "@poscon/shared-frontend";
import {
  pushZStack,
  setViewDimension,
  viewDimensionSelector,
  viewPositionSelector,
  zStackSelector,
} from "~redux/slices/edstSlice";
import type { EdstView } from "types/edstView";
import { useDragging } from "hooks/useDragging";
import { useFullscreen } from "hooks/useFullscreen";
import {
  Container as PixiContainer,
  FederatedEventHandler,
  FederatedPointerEvent,
  Sprite as PixiSprite,
} from "pixi.js";
import { useEventListener, useWindowSize } from "usehooks-ts";
import { headerRowHeight } from "~/utils/constants";
import { Rectangle, Texture } from "pixi.js";
import type { AnchorDirection } from "hooks/useResizable";
import { getCursorDirectionFromAnchor, resizeCursorCenterMap, useResizable } from "hooks/useResizable";
import { InteractiveContainer } from "./InteractiveContainer";

const viewWithMask: EdstView[] = ["ACL", "DEP", "PLANS_DISPLAY"];

export type HeaderComponentProps = {
  focused: boolean;
  toggleFullscreen: () => void;
  width: number;
  startDrag: FederatedEventHandler;
};

export type BodyComponentProps = {
  width: number;
  height: number;
};

type FullscreenWindowProps = {
  view: EdstView;
  HeaderComponent: ComponentType<HeaderComponentProps>;
  BodyComponent: ComponentType<BodyComponentProps>;
  minWidth: number;
};

const headerComponentHeight = eramFontDimensionMap[eramFontNameMap[2]].height + 2;

export const FullscreenView = ({ view, HeaderComponent, BodyComponent, minWidth }: FullscreenWindowProps) => {
  const windowSize = useWindowSize();
  const dispatch = useRootDispatch();
  const ref = useRef<PixiContainer>(null);
  const maskRef = useRef<PixiSprite>(null);
  const focused = useFocused(ref);
  const zStack = useRootSelector(zStackSelector);
  const pos = useRootSelector((state) => viewPositionSelector(state, view));
  const dim = useRootSelector((state) => viewDimensionSelector(state, view));
  const { startDrag, anyDragging } = useDragging(ref, view, "mouseup", false);
  const { isFullscreen, toggleFullscreen } = useFullscreen(view);
  const { startResize } = useResizable(ref, view);
  const toolbarPosition = useRootSelector((state) => state.edst.toolbarPosition);

  const x = isFullscreen ? 2 : pos.x;
  const y = isFullscreen ? (toolbarPosition === "top" ? headerRowHeight + 4 : 2) : pos.y;
  const width = Math.max(minWidth, isFullscreen || dim.width === 0 ? windowSize.width - 3 : dim.width);
  const height = isFullscreen || dim.height === 0 ? windowSize.height - headerRowHeight - 6 : dim.height;

  const resizeClickHandler = (event: FederatedPointerEvent) => {
    if (zStack.indexOf(view) < zStack.length - 1 && !isFullscreen) {
      dispatch(pushZStack(view));
    }
    if (ref.current) {
      let anchorX: AnchorDirection = 0;
      let anchorY: AnchorDirection = 0;
      if (Math.abs(y + 1 - event.global.y) < 5) {
        anchorY = -1;
      }
      if (Math.abs(y + 1 + height - event.global.y) < 5) {
        anchorY = 1;
      }
      if (Math.abs(x + 1 - event.global.x) < 5) {
        anchorX = -1;
      }
      if (Math.abs(x + 1 + width - event.global.x) < 5) {
        anchorX = 1;
      }
      if (anchorX !== 0 || anchorY !== 0) {
        void startResize(event, anchorX, anchorY);
      }
    }
  };

  useEventListener("mousemove", (event) => {
    if (!anyDragging && ref.current) {
      let anchorX: AnchorDirection = 0;
      let anchorY: AnchorDirection = 0;
      if (Math.abs(y + 1 - event.pageY) < 5) {
        anchorY = -1;
      }
      if (Math.abs(y + 1 + height - event.pageY) < 5) {
        anchorY = 1;
      }
      if (Math.abs(x + 1 - event.pageX) < 5) {
        anchorX = -1;
      }
      if (Math.abs(x + 1 + width - event.pageX) < 5) {
        anchorX = 1;
      }
      const cursorDirection = getCursorDirectionFromAnchor(anchorX, anchorY);
      if (cursorDirection) {
        const cursorCenter = resizeCursorCenterMap[cursorDirection].join(" ");
        ref.current.cursor = `url(/cursors/EDSTResizeArrow${cursorDirection}.png) ${cursorCenter}, auto`;
      } else {
        ref.current.cursor = "";
      }
    }
  });

  useEffect(() => {
    if (dim.width === 0 || dim.height === 0) {
      dispatch(setViewDimension({ view, dim: { width, height } }));
    } else if (dim.width < minWidth) {
      dispatch(setViewDimension({ view, dim: { width: minWidth, height } }));
    }
  }, [dim, dispatch, height, view, width]);

  const showMask = viewWithMask.includes(view);

  return (
    <InteractiveContainer
      ref={ref}
      x={x}
      y={y}
      onMouseDown={resizeClickHandler}
      zIndex={zStack.indexOf(view)}
      hitArea={new Rectangle(-2, -2, width + 4, height + 4)}
      sortableChildren
      isRenderGroup
    >
      <graphics
        draw={(graphics) => {
          graphics.clear();
          graphics.rect(0, 0, width, height).fill(0).stroke({ width: 1, color: colorNameMap.grey });
          graphics
            .rect(2, 2, width - 4, height - 4)
            .stroke({ width: 1, color: colorNameMap.mediumGrey });
        }}
        eventMode="none"
        zIndex={0}
      />
      {showMask && <sprite texture={Texture.WHITE} x={2} y={3} width={width - 4} height={height - 6} ref={maskRef} eventMode="none" />}
      <container zIndex={1} x={2} y={3} mask={showMask ? maskRef.current : undefined}>
        <HeaderComponent
          width={width - 6}
          focused={focused}
          toggleFullscreen={toggleFullscreen}
          startDrag={startDrag}
        />
        <BodyComponent width={width - 4} height={height - 4 - headerComponentHeight} />
      </container>
    </InteractiveContainer>
  );
};
